import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import { downloadTemplate } from "../components/LeadsImportModal";
import { fetchAgents, type Agent } from "../services/agentsService";
import { dispatchCallQueue } from "../services/callsService";
import {
  fetchCampaigns,
  launchOutboundCampaign,
  type Campaign,
  type LaunchCampaignResult,
} from "../services/campaignsService";
import { leadDate } from "../services/leadsService";

// ===========================================================================
// Campañas de llamadas salientes (issue #220 B). Upload an Excel/CSV of
// prospects → launch a throttled outbound AI-call campaign with a chosen
// presentation agent. The backend creates the leads AND enqueues the calls via
// the call queue (concurrency / idempotency / do-not-call enforced server-side).
// ===========================================================================

function fmtDate(value: Campaign["createdAt"]): string {
  const d = leadDate(value);
  if (!d) return "—";
  return d.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function CampaignsOutbound() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLaunch, setShowLaunch] = useState(false);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [camps, agentsPage] = await Promise.all([fetchCampaigns(), fetchAgents({ type: "ai", status: "active" })]);
      setCampaigns(camps);
      setAgents(agentsPage.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudieron cargar las campañas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const agentName = useCallback(
    (id: string | null) => (id ? agents.find((a) => a.id === id)?.name || "—" : "—"),
    [agents],
  );

  const processQueue = async () => {
    setProcessing(true);
    try {
      const res = await dispatchCallQueue();
      await Swal.fire({
        icon: "success",
        title: "Cola procesada",
        text: res.outsideWindow
          ? "Fuera del horario de marcado: las llamadas siguen en cola y salen solas cuando abra la ventana (por defecto 9:00–20:00, hora de Ciudad de México)."
          : `${res.dispatched} llamada(s) despachada(s). Capacidad libre: ${res.capacity}.`,
        confirmButtonColor: brand.primary,
      });
      await load();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo procesar la cola", confirmButtonColor: brand.primary });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-rocket-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Campañas de llamadas</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Carga un Excel y lanza llamadas salientes con un agente de presentación
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void processQueue()}
              disabled={processing}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-primary-500 ${tc.border} ${tc.cardBg} ${tc.text} disabled:opacity-50`}
              title="Despacha las llamadas en cola respetando el tope de concurrencia"
            >
              <i className={processing ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"} />
              <span className="whitespace-nowrap">Procesar cola</span>
            </button>
            <button
              onClick={() => setShowLaunch(true)}
              disabled={agents.length === 0}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
              title={agents.length === 0 ? "Crea un agente IA activo primero (CRM → Configuración → Agentes)" : "Nueva campaña"}
            >
              <i className="ri-add-line" />
              <span className="whitespace-nowrap">Nueva campaña</span>
            </button>
          </div>
        </div>

        {agents.length === 0 && !loading && (
          <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
            <i className="ri-information-line mr-1" />
            No hay agentes de IA activos. Crea uno en <b>Configuración → Agentes</b> (idealmente con “Base de
            conocimiento” activada para una presentación correcta) antes de lanzar una campaña.
          </div>
        )}

        {/* Campaigns list */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando campañas...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : campaigns.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-rocket-2-line mb-2 block text-3xl" />
              Aún no has lanzado campañas. Pulsa “Nueva campaña”, sube tu Excel y elige un agente.
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    <th className="p-3 text-left font-bold">Campaña</th>
                    <th className="p-3 text-left font-bold">Agente</th>
                    <th className="p-3 text-center font-bold">Leads</th>
                    <th className="p-3 text-center font-bold">En cola</th>
                    <th className="p-3 text-center font-bold">Omitidos</th>
                    <th className="p-3 text-center font-bold">Duplicados</th>
                    <th className="p-3 text-center font-bold">Tope</th>
                    <th className="p-3 text-left font-bold">Lanzada</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c, i) => (
                    <tr
                      key={c.id}
                      className={
                        isDarkMode
                          ? i % 2 === 0 ? "bg-[#232323] text-white" : "bg-[#1C1C1C] text-white"
                          : i % 2 === 0 ? "bg-gray-50 text-gray-900" : "bg-white text-gray-900"
                      }
                    >
                      <td className="p-2 font-medium">{c.name}</td>
                      <td className="p-2">{agentName(c.crmAgentId)}</td>
                      <td className="p-2 text-center">{c.totals?.created ?? 0}</td>
                      <td className="p-2 text-center text-emerald-500">{c.totals?.queued ?? 0}</td>
                      <td className="p-2 text-center text-amber-500">{c.totals?.skipped ?? 0}</td>
                      <td className="p-2 text-center">{c.totals?.duplicates ?? 0}</td>
                      <td className="p-2 text-center">{c.maxCalls ?? "—"}</td>
                      <td className="p-2 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Las llamadas se despachan poco a poco respetando el tope de concurrencia (no se llaman todas a la vez).
          No se vuelve a llamar a quien ya fue contactado o pidió no ser llamado, y los contactos repetidos se omiten.
        </p>
      </div>

      {showLaunch && (
        <LaunchCampaignModal
          tc={tc}
          isDarkMode={isDarkMode}
          agents={agents}
          onClose={() => setShowLaunch(false)}
          onLaunched={load}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Launch modal — upload + agent + caps, then POST /leads/bulk-campaign.
// ---------------------------------------------------------------------------
function LaunchCampaignModal({
  tc,
  isDarkMode,
  agents,
  onClose,
  onLaunched,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  isDarkMode: boolean;
  agents: Agent[];
  onClose: () => void;
  onLaunched: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  // Presentation agents first (product_intro / outbound_sales) — they're grounded.
  const sortedAgents = useMemo(() => {
    const rank = (a: Agent) => (a.useCase === "product_intro" || a.useCase === "outbound_sales" ? 0 : 1);
    return [...agents].sort((a, b) => rank(a) - rank(b));
  }, [agents]);
  const [agentId, setAgentId] = useState(sortedAgents[0]?.id ?? "");
  const [maxCalls, setMaxCalls] = useState("");
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LaunchCampaignResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | null) => {
    setError(null);
    setResult(null);
    if (!f) return setFile(null);
    if (!/\.(xlsx|csv)$/i.test(f.name)) {
      setError("Formato no soportado. Usa un archivo .xlsx o .csv.");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file || !agentId) return;
    setLaunching(true);
    setError(null);
    try {
      const max = maxCalls.trim() ? Number(maxCalls) : null;
      const res = await launchOutboundCampaign({
        file,
        crmAgentId: agentId,
        campaignName: name.trim() || "Campaña sin nombre",
        maxCalls: max && max > 0 ? Math.floor(max) : null,
      });
      setResult(res);
      onLaunched();
    } catch (e) {
      const err = e as { response?: { data?: { data?: { message?: string }; message?: string } }; message?: string };
      setError(
        err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          "No se pudo lanzar la campaña",
      );
    } finally {
      setLaunching(false);
    }
  };

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;

  return (
    <Modal
      title="Nueva campaña de llamadas"
      onClose={onClose}
      themeClasses={tc}
      content={
        <div className="space-y-4">
          {/* Template */}
          <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <p className={`text-sm font-semibold ${tc.text}`}>1. Plantilla de contactos</p>
            <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>
              Una fila por prospecto. <b>Nombre</b> y al menos <b>WhatsApp</b> (o teléfono) para poder llamar.
            </p>
            <button
              onClick={() => void downloadTemplate()}
              className="mt-2 inline-flex items-center gap-2 rounded-lg border border-emerald-500 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10"
            >
              <i className="ri-download-2-line" /> Descargar plantilla (.xlsx)
            </button>
          </div>

          {/* Config */}
          <div className={`space-y-3 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <p className={`text-sm font-semibold ${tc.text}`}>2. Configura la campaña</p>
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Nombre de la campaña</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Prospectos feria logística" className={inputClass} />
            </div>
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Agente de presentación</label>
              <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className={inputClass}>
                {sortedAgents.length === 0 && <option value="">Sin agentes IA activos</option>}
                {sortedAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.useKnowledgeBase ? " · 📚" : ""}
                    {a.product ? ` (${a.product})` : ""}
                  </option>
                ))}
              </select>
              <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                La voz la define el agente. Las llamadas salen del número de origen configurado para la plataforma (aún no se elige por campaña). 📚 = usa la base de conocimiento.
              </p>
            </div>
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Tope de llamadas (opcional)</label>
              <input
                type="number"
                min={1}
                value={maxCalls}
                onChange={(e) => setMaxCalls(e.target.value)}
                placeholder="Sin tope"
                className={inputClass}
              />
              <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                Limita cuántos contactos del archivo se encolan para llamar.
              </p>
            </div>
          </div>

          {/* Upload */}
          <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <p className={`text-sm font-semibold ${tc.text}`}>3. Sube el archivo y lanza</p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
              className={`${inputClass} mt-2`}
            />
            {file && (
              <p className={`mt-2 text-xs ${tc.textSecondary}`}>
                <i className="ri-attachment-2 mr-1" />
                {file.name}
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          )}

          {result && (
            <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`mb-2 text-sm font-semibold ${tc.text}`}>Campaña lanzada</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat label="Creados" value={result.created} tone="text-emerald-500" />
                <Stat label="En cola" value={result.queued} tone="text-sky-500" />
                <Stat label="Omitidos" value={result.skipped} tone="text-amber-500" />
                <Stat label="Duplicados" value={result.duplicates} tone="text-gray-400" />
              </div>
              {result.errors.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-amber-500/30 bg-amber-500/5 p-2">
                  <p className="mb-1 text-xs font-medium text-amber-600 dark:text-amber-400">Filas con problemas:</p>
                  <ul className={`space-y-0.5 text-xs ${tc.textSecondary}`}>
                    {result.errors.map((err, i) => (
                      <li key={i}>
                        {err.row ? `Fila ${err.row}: ` : ""}
                        {err.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}>
              {result ? "Cerrar" : "Cancelar"}
            </button>
            {!result && (
              <button
                onClick={() => void submit()}
                disabled={!file || !agentId || launching}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className={launching ? "ri-loader-4-line animate-spin" : "ri-rocket-2-line"} />
                {launching ? "Lanzando..." : "Lanzar campaña"}
              </button>
            )}
          </div>
        </div>
      }
    />
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg border border-gray-200/40 p-2 dark:border-gray-700/40">
      <div className={`text-lg font-bold ${tone}`}>{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
    </div>
  );
}
